import { db } from './firebase';
import { 
  ref, 
  push, 
  set, 
  onValue, 
  serverTimestamp,
  runTransaction,
  onDisconnect
} from 'firebase/database';

class AnalyticsService {
  constructor(venueId = 'celtic-druid') {
    this.venueId = venueId;
    this.sessionId = this.generateSessionId();
    this.sessionStart = Date.now();
    this.sessionRef = null;
  }

  generateSessionId() {
    if (typeof window === 'undefined') return 'ssr';
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Initialize visitor tracking
  async initializeTracking() {
    if (typeof window === 'undefined') return;

    this.sessionRef = ref(db, `venues/${this.venueId}/sessions/${this.sessionId}`);
    
    const sessionData = {
      timestamp: Date.now(),
      startTime: this.sessionStart,
      device: this.getDeviceType(),
      referrer: document.referrer || 'direct',
      page: window.location.pathname
    };

    await set(this.sessionRef, sessionData);
    
    // Auto-remove on disconnect
    onDisconnect(this.sessionRef).remove();

    // Track session end
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.trackEvent('session_end', {
          duration: Date.now() - this.sessionStart
        });
      });
    }
  }

  // Generic event tracking
  async trackEvent(eventType, data = {}) {
    try {
      const eventRef = push(ref(db, `venues/${this.venueId}/events`));
      
      const eventData = {
        type: eventType,
        timestamp: Date.now(),
        sessionId: this.sessionId,
        data
      };

      await set(eventRef, eventData);
      await this.updateAggregatedStats(eventType, data);
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }

  // Photo view tracking
  async trackPhotoView(photo) {
    await this.trackEvent('photo_view', {
      photoId: photo.id,
      photoYear: photo.year,
      timestamp: Date.now()
    });

    const viewsRef = ref(db, `venues/${this.venueId}/stats/photos/${photo.id}/views`);
    await runTransaction(viewsRef, (current) => (current || 0) + 1);

    // Update total views
    const totalViewsRef = ref(db, `venues/${this.venueId}/stats/totalViews`);
    await runTransaction(totalViewsRef, (current) => (current || 0) + 1);
  }

  // Photo download tracking
  async trackPhotoDownload(photo) {
    await this.trackEvent('photo_download', {
      photoId: photo.id,
      photoYear: photo.year,
      hasWatermark: true
    });

    const downloadRef = ref(db, `venues/${this.venueId}/stats/totalDownloads`);
    await runTransaction(downloadRef, (current) => (current || 0) + 1);
  }

  // Before/After creation tracking
  async trackBeforeAfterCreated(beforeYear, afterYear) {
    const baId = `ba_${Date.now()}`;
    
    await this.trackEvent('before_after_created', {
      baId,
      beforeYear,
      afterYear,
      yearsDiff: afterYear - beforeYear
    });

    const baRef = ref(db, `venues/${this.venueId}/stats/beforeAfterCreated`);
    await runTransaction(baRef, (current) => (current || 0) + 1);

    return baId;
  }

  // Share tracking
  async trackShare(baId, platform) {
    await this.trackEvent('share', {
      baId,
      platform,
      viral: true
    });

    const shareRef = ref(db, `venues/${this.venueId}/stats/totalShares`);
    await runTransaction(shareRef, (current) => (current || 0) + 1);
  }

  // Update aggregated statistics
  async updateAggregatedStats(eventType, data) {
    const today = new Date().toISOString().split('T')[0];
    const hour = new Date().getHours();
    
    // Daily stats
    const dailyRef = ref(db, `venues/${this.venueId}/aggregated/daily/${today}/${eventType}`);
    await runTransaction(dailyRef, (current) => (current || 0) + 1);
    
    // Hourly stats
    const hourlyRef = ref(db, `venues/${this.venueId}/aggregated/hourly/${today}/${hour}/${eventType}`);
    await runTransaction(hourlyRef, (current) => (current || 0) + 1);
  }

  // Get device type
  getDeviceType() {
    if (typeof window === 'undefined') return 'desktop';
    
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  // Subscribe to live visitors
  subscribeToLiveVisitors(callback) {
    const sessionsRef = ref(db, `venues/${this.venueId}/sessions`);
    
    return onValue(sessionsRef, (snapshot) => {
      const sessions = snapshot.val() || {};
      const now = Date.now();
      
      const activeSessions = Object.entries(sessions).filter(([_, session]) => {
        return session.timestamp && (now - session.timestamp < 300000); // 5 minutes
      });
      
      callback(activeSessions.length);
    });
  }

  // Subscribe to venue stats
  subscribeToStats(callback) {
    const statsRef = ref(db, `venues/${this.venueId}/stats`);
    
    return onValue(statsRef, (snapshot) => {
      const data = snapshot.val() || {};
      callback({
        totalViews: data.totalViews || 0,
        totalDownloads: data.totalDownloads || 0,
        beforeAfterCreated: data.beforeAfterCreated || 0,
        totalShares: data.totalShares || 0,
        photos: data.photos || {}
      });
    });
  }

  // Subscribe to today's stats
  subscribeToDailyStats(callback) {
    const today = new Date().toISOString().split('T')[0];
    const dailyRef = ref(db, `venues/${this.venueId}/aggregated/daily/${today}`);
    
    return onValue(dailyRef, (snapshot) => {
      callback(snapshot.val() || {});
    });
  }
}

// Singleton instance management
let analyticsInstance = null;

export function getAnalytics(venueId) {
  if (!analyticsInstance) {
    analyticsInstance = new AnalyticsService(venueId);
    if (typeof window !== 'undefined') {
      analyticsInstance.initializeTracking();
    }
  }
  return analyticsInstance;
}